// NotificationBellV3 — bell icon + unread count dot + dropdown list.
//
// Backend: GET /api/notifications/recent (mock for anon, real for logged).
// The `mock` flag on the response is surfaced as a subtle "demo" tag in the
// footer of the dropdown so QA can tell them apart at a glance.
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Bell, Sparkles, Mail, AlertTriangle } from "lucide-react";
import { apiFetch } from "@/lib/api";

const ICON_BY_TYPE = { lead: Sparkles, campaign: Mail, credits: AlertTriangle };

function timeAgo(iso) {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}g`;
}

export default function NotificationBellV3() {
    const { t } = useTranslation("header_v3");
    const [open, setOpen] = useState(false);
    const [state, setState] = useState({ items: [], unread: 0, mock: false, loading: true });
    const wrapperRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch("/notifications/recent");
                setState({
                    items: data.items || [],
                    unread: data.unread_count || 0,
                    mock: !!data.mock,
                    loading: false,
                });
            } catch { setState((s) => ({ ...s, loading: false })); }
        })();
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={t("notifications.aria")}
                aria-expanded={open}
                className="relative w-9 h-9 rounded-lg flex items-center justify-center text-white/90 hover:text-white hover:bg-white/[0.06] transition-colors"
                data-testid="header-v3-notification-bell"
            >
                <Bell className="w-[18px] h-[18px]" />
                {state.unread > 0 && (
                    <span
                        className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                        style={{ backgroundColor: "#FAFAFA", color: "#000000", boxShadow: "0 0 0 2px #0A0A0A" }}
                        data-testid="header-v3-notification-badge"
                    >
                        {state.unread > 9 ? "9+" : state.unread}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 2 }}
                        transition={{ duration: 0.16 }}
                        className="absolute right-0 top-full mt-2 w-[340px] rounded-2xl border border-white/5 shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden z-40"
                        style={{ backgroundColor: "#111111" }}
                        data-testid="header-v3-notification-panel"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                            <span className="text-sm font-semibold text-white">{t("notifications.title")}</span>
                            {state.mock && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                                    style={{ backgroundColor: "rgba(255, 255, 255,0.15)", color: "#FFFFFF" }}>
                                    Demo
                                </span>
                            )}
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                            {state.loading && (
                                <div className="px-4 py-8 text-center text-sm text-[#CCCCCC]">…</div>
                            )}
                            {!state.loading && state.items.length === 0 && (
                                <div className="px-4 py-8 text-center text-sm text-[#CCCCCC]">
                                    {t("notifications.empty")}
                                </div>
                            )}
                            {state.items.map((n, i) => {
                                const Icon = ICON_BY_TYPE[n.type] || Bell;
                                return (
                                    <a
                                        key={n.id || i}
                                        href={n.action_url || "#"}
                                        className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors border-b border-white/5 last:border-b-0 no-underline"
                                        data-testid={`header-v3-notification-item-${i + 1}`}
                                    >
                                        <div
                                            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                            style={{ background: "rgba(255, 255, 255,0.12)", border: "1px solid rgba(255, 255, 255,0.25)" }}
                                            aria-hidden
                                        >
                                            <Icon className="w-4 h-4" style={{ color: "#FFFFFF" }} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-white truncate">{n.title}</span>
                                                <span className="text-[10px] text-[#999999] shrink-0">{n.created_at ? timeAgo(n.created_at) : ""}</span>
                                            </div>
                                            <p className="text-xs text-[#CCCCCC] mt-0.5 line-clamp-2">{n.body}</p>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
